import {
  categoriesWithAnswers,
  CategoriesWithAnswers,
  Category,
} from '../data/categories';
import { Game, saturdayGames } from '../data/saturdayGames';
import { Answers, Song } from '../types';

export function intersectionExists(
  cat1: Category,
  cat2: Category,
  categories: CategoriesWithAnswers,
  amount = 1
) {
  return (
    categories[cat1].filter((answer) => categories[cat2].includes(answer))
      .length >= amount
  );
}
export function shuffle<T>(arr: T[]) {
  return arr.sort(() => Math.random() - 0.5);
}

export function checkAnswer(
  category1: Category,
  category2: Category,
  answer: Song
) {
  return (
    categoriesWithAnswers[category1].includes(answer) &&
    categoriesWithAnswers[category2].includes(answer)
  );
}

export function getPossibleAnswers(category1: Category, category2: Category) {
  return categoriesWithAnswers[category1].filter((answer) =>
    categoriesWithAnswers[category2].includes(answer)
  );
}

// Simple seeded pseudo-random number generator
function mulberry32(seed: number): () => number {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Deterministic shuffle using Fisher–Yates
export function seededShuffle<T>(
  array: T[],
  modifier = 0,
  customDate?: Date
): [T[], number] {
  const today = customDate ?? new Date(Date.now());

  const utc = Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    0,
    0,
    0,
    0
  );

  const seed = utc + modifier;

  const rng = mulberry32(seed);
  const arr = [...array];

  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }

  return [arr, seed];
}

export const removeUsedAnswers = (
  answers: Song[],
  usedAnswers: Song[]
): Song[] => {
  return answers.filter((answer) => !usedAnswers.includes(answer));
};

export function restoreUsedAnswers(
  currentUsedAnswers: Song[],
  storedAnswers: Answers
) {
  const storedAnswersArr = Object.values(storedAnswers);

  return [
    ...currentUsedAnswers,
    ...storedAnswersArr.filter(
      (answer) => !currentUsedAnswers.includes(answer)
    ),
  ];
}

export const date = (year: number, month: number, day: number) =>
  new Date(year, month, day).toDateString();

export function appendCustomCategories(
  rows: Category[],
  cols: Category[],
  extraCategories: Category[]
): Category[][] {
  const newRows: Category[] = [...rows];
  const newCols: Category[] = [...cols];

  extraCategories.forEach((cat, i) => {
    // check if extra category already exists in game
    if ([newRows, newCols].flat().includes(cat)) return;

    if (i % 2) {
      newRows[i] = cat;
    } else {
      newCols[i] = cat;
    }
  });

  return [newRows, newCols];
}

export function getWeekNumber(date: Date) {
  const startOfYear = new Date(date.getFullYear(), 0, 1);
  const diff = Number(date) - Number(startOfYear);
  const oneWeek = 1000 * 60 * 60 * 24 * 7;
  return Math.ceil(diff / oneWeek);
}

export function getSaturdayGame(date: Date): Game {
  const weekNumber = getWeekNumber(date);
  const index = weekNumber % saturdayGames.length;
  const { rows, cols } = saturdayGames[index];

  return { rows, cols };
}
